/**
 * Shipping Cutoff Widget — Web Component
 * Custom isolated file — safe to copy to any theme version
 *
 * Calculates and displays next available ship date based on:
 * - Melbourne time (AEST/AEDT)
 * - Configurable cutoff hour (theme setting: shipping_cutoff_hour)
 * - VIC public holidays (theme setting: vic_public_holidays)
 * - Weekends excluded
 *
 * Hidden automatically when next dispatch is 2+ days away (scenarios 3, 5, 7).
 */

class ShippingCutoff extends HTMLElement {
  connectedCallback() {
    const cutoffHour = parseInt(this.dataset.cutoffHour || '11');
    const raw = this.dataset.publicHolidays || '';
    const publicHolidays = raw.trim().startsWith('[')
      ? JSON.parse(raw)
      : raw.split('\n').map(s => s.trim()).filter(Boolean);
    const message = this.calculateMessage(cutoffHour, publicHolidays);
    if (message !== null) {
      this.querySelector('.shipping-cutoff__message').innerHTML = message;
      this.removeAttribute('hidden');
    }
  }

  getMelbourneTime() {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).formatToParts(new Date());
    const get = type => parseInt(parts.find(p => p.type === type).value);
    return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  }

  isDispatchDay(date, holidays) {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;
    const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    return !holidays.includes(dateStr);
  }

  nextDispatchDay(from, holidays) {
    const d = new Date(from);
    d.setDate(d.getDate() + 1);
    while (!this.isDispatchDay(d, holidays)) d.setDate(d.getDate() + 1);
    return d;
  }

  formatDate(date) {
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  }

  calculateMessage(cutoffHour, holidays, now = this.getMelbourneTime()) {
    if (this.isDispatchDay(now, holidays) && now.getHours() < cutoffHour) {
      return `Ships <strong>today</strong> - ${this.formatDate(now)}`;
    }
    const next = this.nextDispatchDay(now, holidays);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = next.getDate() === tomorrow.getDate() && next.getMonth() === tomorrow.getMonth();
    return isTomorrow
      ? `Ships <strong>tomorrow</strong> - ${this.formatDate(next)}`
      : null;
  }
}

customElements.define('shipping-cutoff', ShippingCutoff);
